import React, { FC, useCallback, useMemo, useState } from 'react';
import { View, Text } from 'react-native';
import { LayoutStrategy } from '@oolio-group/domain';
import { useTranslation } from '@oolio-group/localization';
import { useSession } from '../../hooks/useSession';
import { useKitchenDisplay } from '../../hooks/useKitchenDisplay';
import { useScale } from '../../hooks/useScale';
import {
  getDefaultRowsPerPage,
  getEffectiveColumns,
  getEffectiveRowsPerPage,
  getMaxSupportedColumns,
  getMaxSupportedRows,
} from '../../utils/layoutHelper';
import SectionHeading from './SectionHeading';
import LayoutCard from './LayoutCard';
import GridSizePicker from './GridSizePicker';
import SettingsScreenStyles from './Settings.styles';
import LayoutSettingsStyles from './LayoutSettings.styles';

const LAYOUT_STRATEGIES = [
  LayoutStrategy.SINGLE_ROW,
  LayoutStrategy.DOUBLE_ROW,
  LayoutStrategy.FREE_FLOW,
  LayoutStrategy.GRID,
];

const LayoutSettings: FC = () => {
  const styles = SettingsScreenStyles();
  const layoutStyles = LayoutSettingsStyles();
  const { translate } = useTranslation();
  const { session, setSession } = useSession();
  const { scale } = useScale();
  const { updateKitchenDisplay } = useKitchenDisplay({
    storeId: session?.currentStore?.id,
  });

  const [currentStrategy, setCurrentStrategy] = useState<LayoutStrategy>(
    session?.kitchenDisplay?.layoutStrategy?.strategy ||
      LayoutStrategy.FREE_FLOW,
  );
  const [columnsOnScreen, setColumnsOnScreen] = useState<number>(
    session?.kitchenDisplay?.layoutStrategy?.columnsOnScreen ?? 8,
  );
  const [rowsOnScreen, setRowsOnScreen] = useState<number>(
    session?.kitchenDisplay?.layoutStrategy?.rowsOnScreen ??
      getDefaultRowsPerPage(currentStrategy),
  );

  // The readout has to agree with the preview, which clamps to what the
  // viewport can actually draw.
  const maxSupportedRows = useMemo(() => getMaxSupportedRows(scale), [scale]);
  const maxSupportedColumns = useMemo(
    () => getMaxSupportedColumns(scale),
    [scale],
  );

  const saveGrid = useCallback(
    (columns: number, rows: number, strategy = currentStrategy) => {
      setColumnsOnScreen(columns);
      setRowsOnScreen(rows);
      updateKitchenDisplay({
        id: session?.kitchenDisplay?.id || '',
        layoutStrategy: {
          strategy: strategy,
          columnsOnScreen: columns,
          rowsOnScreen: rows,
        },
      });
      setSession({
        kitchenDisplay: {
          ...session.kitchenDisplay,
          layoutStrategy: {
            strategy: strategy,
            columnsOnScreen: columns,
            rowsOnScreen: rows,
          },
        },
      });
    },
    [currentStrategy, session.kitchenDisplay, setSession, updateKitchenDisplay],
  );

  const onSelectStrategy = useCallback(
    (strategy: LayoutStrategy) => {
      if (strategy === currentStrategy) return;
      // Non-grid strategies only ever render their default number of rows, and
      // the row pills disable every other option. Snap the stored value across
      // so the displayed grid size cannot drift away from what is rendered.
      // This goes out as one complete payload: two separate mutations could
      // land out of order and leave `rowsOnScreen` missing from the record.
      const snappedRows =
        strategy === LayoutStrategy.GRID
          ? rowsOnScreen
          : getDefaultRowsPerPage(strategy);

      setCurrentStrategy(strategy);
      saveGrid(columnsOnScreen, snappedRows, strategy);
    },
    [columnsOnScreen, currentStrategy, rowsOnScreen, saveGrid],
  );

  const onChangeColumns = useCallback(
    (columns: number) => saveGrid(columns, rowsOnScreen),
    [rowsOnScreen, saveGrid],
  );

  const onChangeRows = useCallback(
    (rows: number) => saveGrid(columnsOnScreen, rows),
    [columnsOnScreen, saveGrid],
  );

  return (
    <View style={styles.panel} ph-label="settings-layout">
      <View style={styles.panelSection}>
        <SectionHeading
          testID="heading-ticketLayout"
          title={translate('kitchenDisplay.ticketLayout')}
        />
        <View style={layoutStyles.cardGrid}>
          {LAYOUT_STRATEGIES.map(strategy => (
            <LayoutCard
              key={strategy}
              strategy={strategy}
              testID={`layout-card-${strategy.toLowerCase()}`}
              selected={currentStrategy === strategy}
              onSelect={onSelectStrategy}
            />
          ))}
        </View>
      </View>
      <View style={styles.panelSection}>
        <View style={layoutStyles.headingRow}>
          <SectionHeading
            testID="heading-gridSize"
            title={translate('kitchenDisplay.gridSize')}
          />
          <Text testID="grid-size-value" style={layoutStyles.headingValue}>
            {`${getEffectiveColumns(
              columnsOnScreen,
              maxSupportedColumns,
            )} × ${getEffectiveRowsPerPage(
              currentStrategy,
              rowsOnScreen,
              maxSupportedRows,
            )}`}
          </Text>
        </View>
        <GridSizePicker
          strategy={currentStrategy}
          columns={columnsOnScreen}
          rows={rowsOnScreen}
          onChangeColumns={onChangeColumns}
          onChangeRows={onChangeRows}
        />
      </View>
    </View>
  );
};

export default LayoutSettings;
